import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type PaginatedRequest = {
  search?: string;
  page: number;
  limit: number;
  filterType?: string;
};

export type PaginatedResponse<T> = {
  data: T[];
  pageCount: number; // total pages
};

export type PaginatedFetcher<T> = (
  req: PaginatedRequest
) => Promise<PaginatedResponse<T>>;

export type UseFilterPaginationConfig<T> = {
  enabled: boolean;
  fetcher: PaginatedFetcher<T>;
  limit?: number;
  debounceMs?: number;
  filterType?: string;
};

const MAX_SELECT_ALL_PAGES = 500;

export function useFilterPagination<T>({
  enabled,
  fetcher,
  limit = 50,
  debounceMs = 500,
  filterType,
}: UseFilterPaginationConfig<T>) {
  const [data, setData] = useState<T[]>([]);
  const [pageCount, setPageCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const reqInFlightRef = useRef(false);
  const lastReqIdRef = useRef(0);
  const loadMoreLockRef = useRef(false);
  const fetchAllInFlightRef = useRef(false);
  const skipNextPageFetchRef = useRef(false);
  const searchTimerRef = useRef<number | null>(null);
  const dataRef = useRef<T[]>([]);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const hasMore = useMemo(() => {
    if (!enabled) return false;
    if (!pageCount) return true;
    return page < pageCount;
  }, [enabled, page, pageCount]);

  const reset = useCallback((nextSearch?: string) => {
    lastReqIdRef.current += 1;
    reqInFlightRef.current = false;
    loadMoreLockRef.current = false;

    setSearch(nextSearch ?? '');
    setPage(1);
    setData([]);
    setPageCount(0);
  }, []);

  const fetchPage = useCallback(
    async (pageToFetch: number, searchToFetch: string) => {
      if (!enabled) return;
      if (fetchAllInFlightRef.current) return;
      if (reqInFlightRef.current) return;

      reqInFlightRef.current = true;
      setLoading(true);

      const reqId = ++lastReqIdRef.current;

      try {
        const res = await fetcher({
          page: pageToFetch,
          limit,
          search: searchToFetch?.trim() ? searchToFetch.trim() : undefined,
          filterType,
        });

        if (reqId !== lastReqIdRef.current) return;

        const next = Array.isArray(res?.data) ? res.data : [];
        const nextPageCount = Number(res?.pageCount ?? 0) || 0;

        setPageCount(nextPageCount);

        if (pageToFetch === 1) setData(next);
        else setData((prev) => [...prev, ...next]);
        if (next.length === 0) {
          setPageCount(pageToFetch);
        }
      } finally {
        setLoading(false);
        reqInFlightRef.current = false;
        loadMoreLockRef.current = false;
      }
    },
    [enabled, fetcher, limit, filterType]
  );

  useEffect(() => {
    if (!enabled) return;
    // fetchAllPages already loaded everything; don't refetch (and re-append)
    // the last page when it bumps `page` to the final page number.
    if (skipNextPageFetchRef.current) {
      skipNextPageFetchRef.current = false;
      return;
    }
    fetchPage(page, search);
  }, [enabled, page, search, fetchPage]);

  const loadMore = useCallback(() => {
    if (!enabled) return;
    if (loading) return;
    if (fetchAllInFlightRef.current) return;
    if (reqInFlightRef.current) return;
    if (loadMoreLockRef.current) return;
    if (!hasMore) return;

    loadMoreLockRef.current = true;
    setPage((p) => p + 1);
  }, [enabled, loading, hasMore]);

  const setSearchDebounced = useCallback(
    (value: string) => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);

      searchTimerRef.current = window.setTimeout(() => {
        reset(value);
      }, debounceMs);
    },
    [debounceMs, reset]
  );

  /**
   * Load every page for the current search so Select all can cover all matches.
   */
  const fetchAllPages = useCallback(async (): Promise<T[]> => {
    if (!enabled) return dataRef.current;
    if (fetchAllInFlightRef.current) return dataRef.current;

    fetchAllInFlightRef.current = true;
    setLoading(true);
    const reqId = ++lastReqIdRef.current;

    try {
      let pageNum = 1;
      let all: T[] = [];
      let totalPages = 0;

      do {
        const res = await fetcher({
          page: pageNum,
          limit,
          search: search?.trim() ? search.trim() : undefined,
          filterType,
        });

        if (reqId !== lastReqIdRef.current) return dataRef.current;

        const chunk = Array.isArray(res?.data) ? res.data : [];
        totalPages = Number(res?.pageCount ?? 0) || pageNum;
        if (pageNum === 1) all = [...chunk];
        else all.push(...chunk);

        if (chunk.length === 0) break;
        pageNum += 1;
      } while (pageNum <= totalPages && pageNum <= MAX_SELECT_ALL_PAGES);

      setData(all);
      setPageCount(totalPages || 1);
      const finalPage = Math.max(1, totalPages || 1);
      setPage((prev) => {
        if (prev !== finalPage) skipNextPageFetchRef.current = true;
        return finalPage;
      });
      return all;
    } finally {
      setLoading(false);
      fetchAllInFlightRef.current = false;
      reqInFlightRef.current = false;
      loadMoreLockRef.current = false;
    }
  }, [enabled, fetcher, filterType, limit, search]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, []);

  return {
    data,
    pageCount,
    page,
    search,
    loading,
    hasMore,
    loadMore,
    reset,
    setSearchDebounced,
    fetchAllPages,
  };
}
